/*
 * Krok UXF3 — ceske datum a cas v cele administraci (nalez #44, pravidlo 5).
 *
 * `<input type="date">` a `<input type="datetime-local">` kresli Chromium podle
 * lokalizace OPERACNIHO SYSTEMU, ne podle `lang` dokumentu ani `navigator.language`
 * (overeno v kroku UXR3). Cesky ucet tedy vidi `mm/dd/yyyy` a `05:34 AM`, a aplikace
 * s tim nemuze nic udelat. Proto se datum i cas zadava jako TEXT v ceskem zapisu
 * (`10. 10. 2026`, `05:34`) a tyto funkce prevadeji mezi zobrazenim a strojovou
 * hodnotou, kterou ocekava API i stary kod (`YYYY-MM-DD`, `YYYY-MM-DDTHH:mm`).
 *
 * Vsechny funkce jsou ciste — bez `Date.now()`, bez `Intl` nad vstupem uzivatele
 * (Intl formatuje jen hotovou hodnotu), takze jsou deterministicke v testech.
 */
const pad = (n) => String(n).padStart(2, '0');
/** Rozdeli zapis na skupiny cislic: `10. 10. 2026`, `10.10.2026`, `10/10/2026`, `2026-10-10`. */
function digitTokens(input) {
    return input.split(/[^\d]+/).filter((part) => part !== '');
}
/** Rozdeli zapis na cisla: `10. 10. 2026`, `10.10.2026`, `10/10/2026`, `2026-10-10`. */
function digitGroups(input) {
    return digitTokens(input).map((part) => Number(part));
}
/**
 * Rok ze zapisu data — jen DVOU nebo CTYRMISTNY (krok NOC2 bod 1, nalez N07).
 *
 * `null` = rozepsany prefix, ne rok. Bez teto kontroly cetl parser kazdou
 * delku: `1. 11. 2` dal rok 2002 a `1. 11. 202` rok 202, takze pole, ktere
 * uklada pri psani, poslalo na server dve NESMYSLNE hodnoty jeste nez clovek
 * dopsal rok; `1. 11. 20026` dokonce rok 20026 (pak uz ho zadny formatovac
 * data nepoznal a dokument sazel „dne 20026-11-01").
 */
function yearFromToken(token) {
    if (token.length === 2)
        return 2000 + Number(token);
    return token.length === 4 ? Number(token) : null;
}
/** Existuje takovy den v takovem mesici? (31. 2. neprojde.) */
function isRealDate(year, month, day) {
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return false;
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
/** Strojova hodnota `YYYY-MM-DD` → `10. 10. 2026`. Neplatna hodnota → prazdny retezec. */
export function formatCzechDate(value) {
    if (typeof value !== 'string')
        return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (match === null)
        return '';
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (!isRealDate(year, month, day))
        return '';
    return `${day}. ${month}. ${year}`;
}
/**
 * Cesky zapis → `YYYY-MM-DD`, nebo `null` kdyz to datum neni.
 * Dvoumistny rok se bere jako 20xx (makler pise `10.10.26`).
 */
export function parseCzechDate(input) {
    const text = input.trim();
    if (text === '')
        return null;
    // ISO zapis projde taky — prijde z URL, z API i z nativniho pickeru.
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
    if (iso !== null) {
        const [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
        return isRealDate(year, month, day) ? `${year}-${pad(month)}-${pad(day)}` : null;
    }
    const tokens = digitTokens(text);
    if (tokens.length !== 3)
        return null;
    const [dayText, monthText, yearText] = tokens;
    const year = yearFromToken(yearText);
    if (year === null)
        return null;
    const [day, month] = [Number(dayText), Number(monthText)];
    return isRealDate(year, month, day) ? `${year}-${pad(month)}-${pad(day)}` : null;
}
/**
 * HOTOVY zapis, nebo jen rozepsany prefix? (krok NOC2 bod 1, nalez N07)
 *
 * Pole data uklada uz PRI PSANI, aby se dokument vedle nej menil rovnou. Kazdy
 * prefix delsiho roku je ale taky platne datum (`10. 10. 20` = 20. leta),
 * takze se behem jedineho zadani odeslalo nekolik ruznych hodnot; odpovedi
 * serveru pak chodily v jinem poradi, prepsaly rozepsany text v poli a dalsi
 * stisk klavesy z nich udelal rok `20026`.
 *
 * Hotovy je zapis, ktery se SHODUJE s nekterym kanonickym tvarem teze hodnoty
 * (mezery nerozhoduji) — tedy s ceskym zapisem (`1. 11. 2026`) nebo se
 * strojovou hodnotou (`2026-11-01`, prijde z odkazu a z API). `1. 11. 20` se
 * neshoduje s nicim (kanonicky by byl `1. 11. 2020`), takze je to prefix.
 * Zkracene tvary (`10.10.26`) se tedy ulozi az pri odchodu z pole, kdy uz
 * zadny dalsi znak nepribude.
 */
export function isSettledCzechTemporalInput(input, ...canonicalForms) {
    const bare = (text) => text.replace(/\s+/gu, '');
    return canonicalForms.some((form) => form !== '' && bare(input) === bare(form));
}
/** `HH:mm` → `05:34` (24 h). Jina hodnota → prazdny retezec. */
export function formatCzechTime(value) {
    if (typeof value !== 'string')
        return '';
    const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
    if (match === null)
        return '';
    const [hours, minutes] = [Number(match[1]), Number(match[2])];
    if (hours > 23 || minutes > 59)
        return '';
    return `${pad(hours)}:${pad(minutes)}`;
}
/** `5:34`, `0534`, `5.34` → `05:34`; `5:34 PM` se NEprijima (24 h je jediny zapis). */
export function parseCzechTime(input) {
    const text = input.trim();
    if (text === '')
        return null;
    // 12hodinovy zapis nesmi tise projit jako 05:34 — AM/PM je presne to, co #44 vytyka.
    if (/[a-zA-Z]/.test(text))
        return null;
    const compact = /^(\d{2})(\d{2})$/.exec(text);
    const groups = compact !== null ? [Number(compact[1]), Number(compact[2])] : digitGroups(text);
    if (groups.length === 1 && text.length <= 2) {
        const hours = groups[0];
        return hours <= 23 ? `${pad(hours)}:00` : null;
    }
    if (groups.length !== 2)
        return null;
    const [hours, minutes] = groups;
    if (hours > 23 || minutes > 59)
        return null;
    return `${pad(hours)}:${pad(minutes)}`;
}
/** `YYYY-MM-DDTHH:mm` → `10. 10. 2026 05:34`. */
export function formatCzechDateTime(value) {
    if (typeof value !== 'string')
        return '';
    const [datePart, timePart] = value.trim().split('T');
    const date = formatCzechDate(datePart);
    if (date === '')
        return '';
    const time = formatCzechTime(timePart ?? '');
    return time === '' ? date : `${date} ${time}`;
}
/** `10. 10. 2026 5:34` → `2026-10-10T05:34`. Bez casu se doplni `00:00`. */
export function parseCzechDateTime(input) {
    const text = input.trim();
    if (text === '')
        return null;
    const split = /^(.*?)[\sT]+(\d{1,2}[:.]\d{2})$/.exec(text);
    if (split === null) {
        const dateOnly = parseCzechDate(text);
        return dateOnly === null ? null : `${dateOnly}T00:00`;
    }
    const date = parseCzechDate(split[1]);
    const time = parseCzechTime(split[2]);
    return date === null || time === null ? null : `${date}T${time}`;
}
/** `YYYY-MM` → `10. 2026` (mesicni obdobi u predpisu najmu). */
export function formatCzechMonth(value) {
    if (typeof value !== 'string')
        return '';
    const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
    if (match === null)
        return '';
    const month = Number(match[2]);
    if (month < 1 || month > 12)
        return '';
    return `${month}. ${Number(match[1])}`;
}
/** `10. 2026`, `10/2026`, `2026-10` → `2026-10`. */
export function parseCzechMonth(input) {
    const text = input.trim();
    if (text === '')
        return null;
    const iso = /^(\d{4})-(\d{1,2})$/.exec(text);
    if (iso !== null) {
        const month = Number(iso[2]);
        return month >= 1 && month <= 12 ? `${iso[1]}-${pad(month)}` : null;
    }
    const tokens = digitTokens(text);
    if (tokens.length !== 2)
        return null;
    const [monthText, yearText] = tokens;
    const year = yearFromToken(yearText);
    if (year === null)
        return null;
    const month = Number(monthText);
    return month >= 1 && month <= 12 ? `${year}-${pad(month)}` : null;
}
//# sourceMappingURL=czech-date.js.map